import { useState, useEffect } from "react";
import { buyerNotificationsService, BuyerNotification } from "@/services/buyerNotificationsService";
import { useBuyer } from "@/contexts/BuyerContext";
import { toast } from "@/hooks/use-toast";
import { badgeService } from "@/services/badgeService";

export const useBuyerNotifications = () => {
  const [notifications, setNotifications] = useState<BuyerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { buyerProfile } = useBuyer();

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!buyerProfile?.id) return;
    
    setIsLoading(true);
    try {
      const [notificationsData, unreadCountData] = await Promise.all([
        buyerNotificationsService.getBuyerNotifications(buyerProfile.id),
        buyerNotificationsService.getUnreadCount(buyerProfile.id)
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
      
      // Sync app icon badge with unread count
      badgeService.setBadgeCount(unreadCountData);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: "Error",
        description: "Failed to load notifications",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await buyerNotificationsService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read_status: true }
            : notification
        )
      );
      const newCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newCount);
      
      // Update app icon badge
      badgeService.setBadgeCount(newCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!buyerProfile?.id) return;
    
    try {
      await buyerNotificationsService.markAllAsRead(buyerProfile.id);
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read_status: true }))
      );
      setUnreadCount(0);
      
      // Clear app icon badge
      badgeService.clearBadge();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Set up real-time subscription with Safari error protection
  useEffect(() => {
    if (!buyerProfile?.id) return;

    fetchNotifications();

    let channel: ReturnType<typeof buyerNotificationsService.subscribeToNotifications> | null = null;

    try {
      channel = buyerNotificationsService.subscribeToNotifications(
        buyerProfile.id,
        (payload) => {
          console.log('New buyer notification received:', payload);
          fetchNotifications(); // Refresh notifications when new one arrives
          
          // Show toast with notification details
          const notification = payload.new;
          toast({
            title: notification.content?.title || "New Notification",
            description: notification.content?.message || "You have a new notification",
          });
        }
      );
    } catch (error) {
      // Gracefully handle Safari/iOS SecurityError for WebSocket connections
      console.warn('Failed to subscribe to buyer notifications (Safari security restriction?):', error);
      // App continues to work - notifications load on page refresh
    }

    return () => {
      try {
        if (channel) {
          channel.unsubscribe();
        }
      } catch (error) {
        console.warn('Error unsubscribing from buyer notifications channel:', error);
      }
    };
  }, [buyerProfile?.id]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    fetchNotifications
  };
};
